import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { CircularProgress } from "@mui/material";
import { CardContent, Typography } from "@mhome/ui";
import {
  Edit as EditIcon,
  AccessTime as AccessTimeIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useRecoilValue } from "recoil";
import {
  activeScopeState,
  scopePreferenceState,
  wsReadyState,
} from "../global-state";
import { useWebSocket } from "../ws/websocket";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import {
  listCircadianWidgets,
  saveCircadianWidget,
  deleteCircadianWidget,
  updateCircadianConfig,
} from "../service/widget-service";
import ResponsiveCardGrid from "../common/responsive-card-grid";
import { useToast } from "../common/toast";
import CircadianEditDialog from "./circadian-edit-dialog";
import CircadianDetailDialog from "./circadian-detail-dialog";
import CircadianDeleteDialog from "./circadian-delete-dialog";
import { getTagIcon } from "../common/tag";
import { useCustomFormat } from "../views/after-init";
import dayjs from "dayjs";
import MenuCard from "../component/menu-card";
import { createGlobalStyle } from "styled-components";

const GlobalStyle = createGlobalStyle`
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-1px); }
    20%, 40%, 60%, 80% { transform: translateX(1px); }
  }
`;

const CircadianView = forwardRef((props, ref) => {
  const wsReady = useRecoilValue(wsReadyState);
  const { sendAndWaitWs } = useWebSocket();
  const activeScope = useRecoilValue(activeScopeState);
  const scopePreference = useRecoilValue(scopePreferenceState);
  const { successToast, errorToast } = useToast();
  const getComponentBgColor = useComponentBackgroundColor();
  const customFormat = useCustomFormat();

  // Helper function to format value based on tag type
  const formatValue = (value, tag) => {
    switch (tag) {
      case "brightness":
        if (!value) value = "0";
        const brightnessNum = parseFloat(value);
        if (!isNaN(brightnessNum)) {
          return `${Math.round(brightnessNum)}%`;
        }
        return value; // Already formatted
      case "colorTemperature":
        if (!value) value = "0";
        const colorTempNum = parseFloat(value);
        if (!isNaN(colorTempNum)) {
          return `${Math.round(colorTempNum)}K`;
        }
        return value; // Already formatted
      case "colorHue":
        if (!value) value = "0";
        const colorHueNum = parseFloat(value);
        if (!isNaN(colorHueNum)) {
          return `${Math.round(colorHueNum)}°`;
        }
        return value; // Already formatted
      case "temperature":
      case "temperatureDifference": {
        const preferredUnit =
          scopePreference?.[activeScope]?.temperatureUnit || "C";
        if (!value) value = "0" + preferredUnit;
        const match = value.match(/^([+-]?[0-9.]+)([CF])$/);
        if (!match) {
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            return `${Math.round(numValue)}°${preferredUnit}`;
          }
          return value; // Return as-is if can't parse
        }

        const [, numStr, unit] = match;
        const numValue = parseFloat(numStr);

        let displayValue = numValue;
        let displayUnit = unit;

        // Convert between units if needed
        if (unit === "C" && preferredUnit === "F") {
          displayValue = (numValue * 9) / 5 + 32;
          displayUnit = "F";
        } else if (unit === "F" && preferredUnit === "C") {
          displayValue = ((numValue - 32) * 5) / 9;
          displayUnit = "C";
        } else {
          displayUnit = preferredUnit; // Use preferred unit symbol
        }

        return `${Math.round(displayValue)}°${displayUnit}`;
      }
      default:
        const numValue = parseFloat(value);
        return isNaN(numValue) ? value : Math.round(numValue).toString();
    }
  };

  // Helper function to get category icon
  const getCategoryIcon = (tag) => {
    // Map circadian tags to tag.js tags
    const tagMapping = {
      brightness: "brightness",
      colorTemperature: "colorTemperature",
      temperature: "temperature",
      temperatureDifference: "temperatureDifference",
    };

    const mappedTag = tagMapping[tag] || tag;
    return getTagIcon(mappedTag, 14);
  };

  // Helper function to get category label
  const getCategoryLabel = (tag) => {
    switch (tag) {
      case "brightness":
        return "Brightness";
      case "colorTemperature":
        return "Color Temperature";
      case "colorHue":
        return "Color Hue";
      case "temperature":
        return "Temperature";
      case "temperatureDifference":
        return "Temperature Difference";
      default:
        return tag;
    }
  };

  const [circadianWidgets, setCircadianWidgets] = useState([]);
  const [loading, setLoading] = useState(true);

  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shakingCardId, setShakingCardId] = useState(null);

  // Expose handleAdd method to parent via ref
  useImperativeHandle(ref, () => ({
    handleAdd: handleNewCircadianWidget,
  }));

  useEffect(() => {
    if (wsReady) {
      fetchCircadianWidgets();
    }
  }, [wsReady]);

  const fetchCircadianWidgets = async () => {
    setLoading(true);
    try {
      const response = await listCircadianWidgets(sendAndWaitWs);
      if (response.error) {
        errorToast(response.error);
        setCircadianWidgets([]);
        return [];
      } else {
        // Response structure: { widgets: [UiCircadianWidget] }
        const widgetsData = Array.isArray(response?.widgets)
          ? response.widgets.map((widget) => ({
              id: widget.id,
              name: widget.name || `Circadian ${widget.id}`,
              tag: widget.tag || "",
              value: widget.value || "",
              next: widget.next || "",
              nextTimestamp: widget.nextTimestamp || null,
              location: widget.location || null,
              dataType: widget.dataType || "",
              schedules: widget.schedules,
              curve: widget.curve || "Default",
              multiplier: widget.multiplier || 1,
              offset: widget.offset || "",
            }))
          : [];
        const sortedWidgets = widgetsData.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setCircadianWidgets(sortedWidgets);
        return sortedWidgets;
      }
    } catch (error) {
      console.error("Failed to fetch circadian widgets:", error);
      errorToast("Failed to fetch circadian schedules");
      setCircadianWidgets([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleNewCircadianWidget = () => {
    setEditingWidget(null);
    setIsCreating(true);
    setShowEditDialog(true);
  };

  const handleEditCircadianWidget = (event, widget) => {
    event.stopPropagation();
    setEditingWidget(widget);
    setIsCreating(false);
    setShowEditDialog(true);
  };

  const handleSaveWidget = async (widgetData) => {
    setSaving(true);
    try {
      const response = await saveCircadianWidget(sendAndWaitWs, widgetData);
      console.log(response);
      if (response.error) {
        errorToast(response.error);
      } else {
        successToast(
          isCreating
            ? "Circadian schedule created successfully"
            : "Circadian schedule updated successfully"
        );
        setShowEditDialog(false);
        setEditingWidget(null);
        setIsCreating(false);
        fetchCircadianWidgets();
      }
    } catch (error) {
      console.error("Failed to save circadian widget:", error);
      errorToast("Failed to save circadian schedule");
    } finally {
      setSaving(false);
    }
  };

  const handleCloseEditDialog = () => {
    setShowEditDialog(false);
    setEditingWidget(null);
    setIsCreating(false);
  };

  const handleDeleteWidget = async (widgetId) => {
    try {
      const resp = await deleteCircadianWidget(sendAndWaitWs, widgetId);
      if (resp && resp.success) {
        successToast("Circadian schedule deleted");
        setCircadianWidgets((prev) =>
          prev.filter((widget) => widget.id !== widgetId)
        );
        setShowEditDialog(false);
        setEditingWidget(null);
        setIsCreating(false);
      } else {
        errorToast("Failed to delete circadian schedule");
      }
    } catch (error) {
      console.error("Failed to delete circadian widget:", error);
      errorToast("Failed to delete circadian schedule");
    }
  };

  const handleUpdateWidget = async (widgetData) => {
    try {
      // Optimistic update
      setDetailWidget(widgetData);
      setCircadianWidgets((prev) =>
        prev.map((w) => (w.id === widgetData.id ? widgetData : w))
      );

      const response = await updateCircadianConfig(
        sendAndWaitWs,
        widgetData.id,
        widgetData.curve,
        widgetData.offset,
        widgetData.multiplier
      );
      if (response.error) {
        errorToast(response.error);
        fetchCircadianWidgets(); // Revert on error
      } else {
        // Silently refresh data from server to get latest computed values
        const freshWidgets = await fetchCircadianWidgets();
        // Update detail widget with fresh data
        const updatedWidget = freshWidgets.find((w) => w.id === widgetData.id);
        if (updatedWidget) {
          setDetailWidget(updatedWidget);
        }
      }
    } catch (error) {
      console.error("Failed to update circadian widget:", error);
      errorToast("Failed to update");
      fetchCircadianWidgets();
    }
  };

  // Detail dialog state
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [detailWidget, setDetailWidget] = useState(null);

  // Delete dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteWidget, setDeleteWidget] = useState(null);

  const openDetailDialog = (widget) => {
    setDetailWidget(widget);
    setShowDetailDialog(true);
  };

  const closeDetailDialog = () => {
    setShowDetailDialog(false);
    setDetailWidget(null);
  };

  const openDeleteDialog = (widget) => {
    setDeleteWidget(widget);
    setShowDeleteDialog(true);
  };

  const closeDeleteDialog = () => {
    setShowDeleteDialog(false);
    setDeleteWidget(null);
  };

  const confirmDelete = async () => {
    if (deleteWidget?.id) {
      await handleDeleteWidget(deleteWidget.id);
      closeDeleteDialog();
    }
  };

  // Create menu items for a widget
  const getMenuItems = (widget) => [
    {
      label: "Edit",
      icon: <EditIcon style={{ fontSize: 18 }} />,
      onClick: () => {
        setEditingWidget(widget);
        setIsCreating(false);
        setShowEditDialog(true);
      },
    },
    {
      label: "Delete",
      icon: <DeleteIcon style={{ fontSize: 18 }} />,
      onClick: () => {
        openDeleteDialog(widget);
      },
      color: "hsl(var(--destructive))",
    },
  ];

  const handleCardLongPress = (widgetId) => {
    setShakingCardId(widgetId);
    setTimeout(() => setShakingCardId(null), 500);
  };

  // Helper function to convert MUI spacing to pixels
  const spacingToPx = (value) => {
    if (typeof value === "number") {
      return `${value * 8}px`;
    }
    return value;
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: spacingToPx(3),
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <GlobalStyle />
      <div
        style={{
          flexGrow: 1,
          margin: spacingToPx(2),
          paddingBottom: spacingToPx(12),
        }}
      >
        {circadianWidgets.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              width: "100%",
              padding: spacingToPx(3),
            }}
          >
            <AccessTimeIcon
              className="text-muted-foreground"
              style={{
                fontSize: 48,
                marginBottom: spacingToPx(2),
              }}
            />
            <Typography
              variant="h6"
              className="text-muted-foreground"
              style={{
                marginBottom: spacingToPx(1),
              }}
            >
              No Circadian Schedules
            </Typography>
            <Typography variant="body1" className="text-muted-foreground">
              Create your first circadian schedule using the add button above
            </Typography>
          </div>
        ) : (
          <ResponsiveCardGrid
            spacing={2}
            breakpoints={{ xs: 1, sm: 2, md: 3, lg: 4 }}
          >
            {circadianWidgets.map((widget) => (
              <MenuCard
                key={widget.id}
                menuItems={getMenuItems(widget)}
                onCardClick={() => openDetailDialog(widget)}
                onLongPress={() => handleCardLongPress(widget.id)}
                style={{
                  backgroundColor: getComponentBgColor("hsl(var(--card))"),
                  animation:
                    shakingCardId === widget.id
                      ? "shake 0.5s ease-in-out"
                      : "none",
                }}
              >
                <CardContent
                  style={{
                    padding: "12px",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    style={{
                      fontWeight: 500,
                      fontSize: "0.875rem",
                      lineHeight: 1.2,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      marginBottom: spacingToPx(0.5),
                    }}
                  >
                    {widget.name}
                  </Typography>

                  {/* Current Value - Prominent Display */}
                  {widget.value && (
                    <Typography
                      variant="h5"
                      className="text-primary"
                      style={{
                        fontWeight: 600,
                        marginBottom: spacingToPx(0.5),
                      }}
                    >
                      {formatValue(widget.value, widget.tag)}
                    </Typography>
                  )}

                  {/* Next Time - Secondary Info */}
                  {widget.nextTimestamp && (
                    <Typography
                      variant="caption"
                      className="text-muted-foreground"
                      style={{
                        fontSize: "0.75rem",
                      }}
                    >
                      Next: {customFormat(widget.nextTimestamp, dayjs(), true)}
                    </Typography>
                  )}
                </CardContent>
              </MenuCard>
            ))}
          </ResponsiveCardGrid>
        )}

        {/* Edit Dialog */}
        <CircadianEditDialog
          open={showEditDialog}
          onClose={handleCloseEditDialog}
          widget={editingWidget}
          onSave={handleSaveWidget}
          onDelete={handleDeleteWidget}
          isCreate={isCreating}
        />

        {/* Detail Dialog */}
        <CircadianDetailDialog
          open={showDetailDialog}
          onClose={closeDetailDialog}
          widget={detailWidget}
          onEdit={(widget) => {
            closeDetailDialog();
            setEditingWidget(widget);
            setIsCreating(false);
            setShowEditDialog(true);
          }}
          onUpdate={handleUpdateWidget}
        />

        {/* Delete Dialog */}
        <CircadianDeleteDialog
          open={showDeleteDialog}
          onClose={closeDeleteDialog}
          onConfirm={confirmDelete}
          widgetName={deleteWidget?.name}
        />
      </div>
    </>
  );
});

CircadianView.displayName = "CircadianView";

export default CircadianView;
